'use client';

import { useMemo, useState } from 'react';
import { PencilIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react';

import type { ApiSchemas } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { useVoiceSettingsCtx } from './provider';

interface NotificationScenario {
  name: string;
  description: string;
  recipients: string[];
}

interface NotificationsState {
  global_recipients: string[];
  include: ApiSchemas['NotificationInclude'];
  scenarios: NotificationScenario[];
}

export function NotificationsSettings() {
  const { settings, onChange } = useVoiceSettingsCtx();
  const [newEmail, setNewEmail] = useState('');

  const notifications: NotificationsState = useMemo(() => {
    const base = (settings.notifications ?? {}) as Partial<NotificationsState>;
    return {
      global_recipients: base.global_recipients ?? [],
      include: {
        first_name: base.include?.first_name ?? true,
        last_name: base.include?.last_name ?? true,
        phone_number: base.include?.phone_number ?? true,
        email_address: base.include?.email_address ?? true,
        address: base.include?.address ?? true,
        issue_type: base.include?.issue_type ?? true,
        summary: base.include?.summary ?? true,
        transcript: base.include?.transcript ?? true,
        recording_url: base.include?.recording_url ?? true,
      },
      scenarios: base.scenarios ?? [],
    } as NotificationsState;
  }, [settings.notifications]);

  const globalEmails = notifications.global_recipients;

  const addGlobalEmail = () => {
    if (!newEmail) return;
    if (globalEmails.includes(newEmail)) return;
    onChange({
      notifications: {
        ...settings.notifications,
        global_recipients: [...globalEmails, newEmail],
      },
    });
    setNewEmail('');
  };

  const removeGlobalEmail = (email: string) => {
    onChange({
      notifications: {
        ...settings.notifications,
        global_recipients: globalEmails.filter((e) => e !== email),
      },
    });
  };

  const updateInclude = (key: keyof ApiSchemas['NotificationInclude'], value: boolean) => {
    onChange({
      notifications: {
        ...settings.notifications,
        include: { ...notifications.include, [key]: value },
      },
    });
  };

  return (
    <div className='flex w-full flex-col gap-6 rounded-lg border p-4'>
      {/* Global Recipients Section */}
      <div className='space-y-4'>
        <div>
          <h3 className='text-lg font-medium'>Global Recipients</h3>
          <p className='text-sm text-muted-foreground'>
            These email addresses will receive a notification for every single call handled by the Voice AI.
          </p>
        </div>

        <div className='space-y-2'>
          <Label>All Call Emails</Label>
          <div className='flex gap-2'>
            <Input
              placeholder='Add email addresses...'
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addGlobalEmail()}
            />
            <Button onClick={addGlobalEmail}>Add</Button>
          </div>
          <div className='flex flex-wrap gap-2'>
            {globalEmails.map((email) => (
              <Badge key={email} variant='outline' className='flex items-center gap-1'>
                {email}
                <button onClick={() => removeGlobalEmail(email)} className='ml-1'>
                  <XIcon className='h-3 w-3' />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Scenario-Based Notifications Section */}
      <ScenarioSettings />

      {/* Global Email Content Section */}
      <div className='space-y-4'>
        <div>
          <h3 className='text-lg font-medium'>Global Email Content</h3>
          <p className='text-sm text-muted-foreground'>
            Select which information to include in all email notifications. This applies to both global and
            scenario-based emails.
          </p>
        </div>

        <div className='grid grid-cols-3 gap-4'>
          <div className='flex items-center justify-between'>
            <Label htmlFor='first_name'>First Name</Label>
            <Switch
              id='first_name'
              checked={notifications.include.first_name}
              onCheckedChange={(checked) => updateInclude('first_name', checked)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='last_name'>Last Name</Label>
            <Switch
              id='last_name'
              checked={notifications.include.last_name}
              onCheckedChange={(checked) => updateInclude('last_name', checked)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='phone_number'>Phone Number</Label>
            <Switch
              id='phone_number'
              checked={notifications.include.phone_number}
              onCheckedChange={(checked) => updateInclude('phone_number', checked)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='email_address'>Email Address</Label>
            <Switch
              id='email_address'
              checked={notifications.include.email_address}
              onCheckedChange={(checked) => updateInclude('email_address', checked)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='address'>Address</Label>
            <Switch
              id='address'
              checked={notifications.include.address}
              onCheckedChange={(checked) => updateInclude('address', checked)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='issue_type'>Issue Type</Label>
            <Switch
              id='issue_type'
              checked={notifications.include.issue_type}
              onCheckedChange={(checked) => updateInclude('issue_type', checked)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='summary'>Summary</Label>
            <Switch
              id='summary'
              checked={notifications.include.summary}
              onCheckedChange={(checked) => updateInclude('summary', checked)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='transcript'>Transcript</Label>
            <Switch
              id='transcript'
              checked={notifications.include.transcript}
              onCheckedChange={(checked) => updateInclude('transcript', checked)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='recording_url'>Recording URL</Label>
            <Switch
              id='recording_url'
              checked={notifications.include.recording_url}
              onCheckedChange={(checked) => updateInclude('recording_url', checked)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ScenarioSettings() {
  const { settings, onChange } = useVoiceSettingsCtx();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<NotificationScenario | null>(null);
  const [draftRecipient, setDraftRecipient] = useState('');

  const scenarios: NotificationScenario[] = useMemo(
    () =>
      (settings.notifications.scenarios ?? []).map((s) => ({
        name: s.name ?? '',
        description: s.description ?? '',
        recipients: s.recipients ?? [],
      })),
    [settings.notifications]
  );

  const addScenario = () => {
    const newScenario: NotificationScenario = { name: '', description: '', recipients: [] };
    setEditingIndex(null);
    setDraft(newScenario);
    setDraftRecipient('');
    setIsDialogOpen(true);
  };

  const removeScenario = (index: number) => {
    const updated = scenarios.filter((_, i) => i !== index);
    onChange({
      notifications: {
        ...settings.notifications,
        scenarios: updated,
      },
    });
  };

  const removeScenarioRecipient = (index: number, email: string) => {
    const updated = scenarios.map((s, i) =>
      i === index ? { ...s, recipients: s.recipients.filter((r) => r !== email) } : s
    );
    onChange({
      notifications: {
        ...settings.notifications,
        scenarios: updated,
      },
    });
  };

  const beginEditScenario = (scenario: NotificationScenario, index: number) => {
    setEditingIndex(index);
    setDraft({ ...scenario });
    setDraftRecipient('');
    setIsDialogOpen(true);
  };

  const saveScenario = () => {
    if (!draft) return;
    const name = draft.name.trim();
    if (!name) return;
    let updated: NotificationScenario[];
    if (editingIndex !== null) {
      updated = scenarios.map((s, i) => (i === editingIndex ? { ...draft, name } : s));
    } else {
      updated = [...scenarios, { ...draft, name }];
    }
    onChange({
      notifications: {
        ...settings.notifications,
        scenarios: updated,
      },
    });
    setIsDialogOpen(false);
    setDraft(null);
    setEditingIndex(null);
  };

  const removeDraftRecipient = (email: string) => {
    if (!draft) return;
    setDraft({ ...draft, recipients: draft.recipients.filter((r) => r !== email) });
  };

  const addDraftRecipient = () => {
    const value = draftRecipient.trim();
    if (!draft || !value) return;
    if (draft.recipients.includes(value)) return;
    setDraft({ ...draft, recipients: [...draft.recipients, value] });
    setDraftRecipient('');
  };

  return (
    <Card>
      <CardHeader className='flex flex-row items-center justify-between gap-6'>
        <div className='space-y-1.5'>
          <CardTitle>Scenario-Based Notifications</CardTitle>
          <CardDescription>
            Create specific scenarios to notify different people based on call outcomes. These are used in the
            &#39;Notify Representative&#39; action.
          </CardDescription>
        </div>
        <Button variant='outline' size='sm' onClick={addScenario}>
          <PlusIcon className='mr-2 h-4 w-4' />
          Add Scenario
        </Button>
      </CardHeader>
      <CardContent className='flex flex-col gap-4'>
        {scenarios.map((scenario, index) => (
          <div key={index} className='rounded-lg border p-4'>
            <div className='mb-3 flex items-start justify-between'>
              <div className='flex-1'>
                <div className='mb-1 flex items-center gap-2'>
                  <h4 className='font-medium'>{scenario.name || 'Untitled scenario'}</h4>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-6 w-6'
                    onClick={() => beginEditScenario(scenario, index)}
                  >
                    <PencilIcon className='h-3 w-3' />
                  </Button>
                  <Button variant='ghost' size='icon' className='h-6 w-6' onClick={() => removeScenario(index)}>
                    <Trash2Icon className='h-3 w-3' />
                  </Button>
                </div>
                <p className='text-sm text-muted-foreground'>{scenario.description || 'No description provided.'}</p>
              </div>
            </div>

            <div className='space-y-2'>
              <Label>Recipients:</Label>
              <div className='flex flex-wrap gap-2'>
                {scenario.recipients.map((email) => (
                  <Badge key={email} variant='secondary' className='flex items-center gap-1'>
                    {email}
                    <button onClick={() => removeScenarioRecipient(index, email)} className='ml-1'>
                      <XIcon className='h-3 w-3' />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        ))}
      </CardContent>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(v) => {
          setIsDialogOpen(v);
          if (!v) {
            setDraft(null);
            setEditingIndex(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingIndex !== null ? 'Edit Scenario' : 'Add Scenario'}</DialogTitle>
            <DialogDescription>
              Configure the name, description, and recipients for this notification scenario.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label>Name</Label>
              <Input
                placeholder='Scenario name'
                value={draft?.name ?? ''}
                onChange={(e) => draft && setDraft({ ...draft, name: e.target.value })}
              />
            </div>
            <div className='space-y-2'>
              <Label>Description</Label>
              <Textarea
                placeholder='Describe when this notification should be used...'
                value={draft?.description ?? ''}
                onChange={(e) => draft && setDraft({ ...draft, description: e.target.value })}
                className='min-h-[80px]'
              />
            </div>
            <div className='space-y-2'>
              <Label>Recipients</Label>
              <div className='flex flex-wrap gap-2'>
                {(draft?.recipients ?? []).map((email) => (
                  <Badge key={email} variant='secondary' className='flex items-center gap-1'>
                    {email}
                    <button onClick={() => removeDraftRecipient(email)} className='ml-1'>
                      <XIcon className='h-3 w-3' />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className='flex gap-2'>
                <Input
                  placeholder='Add recipient email...'
                  value={draftRecipient}
                  onChange={(e) => setDraftRecipient(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addDraftRecipient();
                    }
                  }}
                />
                <Button size='sm' onClick={addDraftRecipient}>
                  Add
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveScenario} disabled={!draft || !draft.name.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
